import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { PrinterContextProvider_PdfPrint_Fragment } from '../../../contexts/PrinterContext';
import { memoForm2ApiMapper } from '../../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import type { BackError } from '../../../utils/back-errors.util';
import type { MemosState } from '../../../forms/MemoBeforeSubmit';
import type { PrinterContextProvider_PdfPrint } from '../../../contexts/PrinterContext';
import { MessageList } from '../../../components/MessageList';

import { useSavePreviewMutation } from './useSavePreviewMutation.hook';
import { useQueryInvalidation } from './useQueryInvalidation.hook';
import type { SaveMissionReportPreview_Result } from './useSavePreviewMutation.hook';

interface Params {
  readonly missionReportId: Id;
  readonly setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const useSavePreviewHandler = ({
  missionReportId,
  setIsLoading,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: saveReportPreviewMutation } = useSavePreviewMutation();

  const { invalidateQueries } = useQueryInvalidation(missionReportId);

  const onSavePreview = useCallback(
    (
      id: Id,
      memos: MemosState,
      handleSuccess?: (data: PrinterContextProvider_PdfPrint) => void,
      handleError?: () => void,
      // eslint-disable-next-line max-params
    ) => {
      setIsLoading(true);

      const onSuccess = (data: SaveMissionReportPreview_Result) => {
        setIsLoading(false);
        invalidateQueries();
        if (handleSuccess) {
          const pdfPrint = getFragmentData(
            PrinterContextProvider_PdfPrint_Fragment,
            data.saveReportPreview,
          );

          handleSuccess(pdfPrint);
        }
      };

      const onError = (error: unknown) => {
        setIsLoading(false);
        enqueueSnackbar(
          <MessageList messages={userFriendlyErrors(error as BackError)} />,
          {
            variant: 'error',
          },
        );
        handleError?.();
      };

      saveReportPreviewMutation(
        {
          printableId: id,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError,
          onSuccess,
        },
      );
    },
    [
      enqueueSnackbar,
      invalidateQueries,
      saveReportPreviewMutation,
      setIsLoading,
    ],
  );

  return {
    onSavePreview,
  };
};
